import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { PersonasService } from '@services/personas/personas.service';
import { PropietariosService } from '@services/propietarios/propietarios.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { UpdatePropietarioRequest } from '@models/propietarios/updatePropietarioRequest.model';
import { CreatePropietarioRequest } from '@models/propietarios/createPropietarioRequest.model';
import { CreatePropietarioResponse } from '@models/propietarios/createPropietarioResponse.model';
import { BaseDto } from '@models/base/baseDto.model';
import { PropietarioDto } from '@models/propietarios/propietarioDto.model';
import { PersonaDetailDto } from '@models/personas/personaDetailDto.model';
import { isFalsy } from 'utility-types';
import { DeleteConfirmationModalComponent, DeleteConfirmationModalResult } from 'src/app/component/delete-confirmation-modal/delete-confirmation-modal.component';

@Component({
  selector: 'app-form-propietario-update',
  templateUrl: './form-propietario-update.component.html',
  styleUrls: ['./form-propietario-update.component.css']
})
export class FormPropietarioUpdateComponent implements OnInit {
  propietarioForm!: FormGroup;
  formBuilder: FormBuilder = new FormBuilder();

  // Estados del componente
  modoEdicion: boolean = false;
  datosOriginales: any = null;

  // Datos principales
  propietario!: PropietarioDto;
  personaDetailDto!: PersonaDetailDto;

  // IDs y objetos auxiliares
  idTipoLiquidacion: number = 0;
  idPropietario: number = 0;
  idBanco: number = 0;
  banco: BaseDto = new BaseDto();
  tipoLiquidacion = new BaseDto();

  // Listas para dropdowns
  lstTipoLiquidacionModel: BaseDto[] = [];

  // Requests/Responses
  createPropietarioRequest: CreatePropietarioRequest = new CreatePropietarioRequest();
  updatePropietarioRequest: UpdatePropietarioRequest = new UpdatePropietarioRequest();
  createPropietarioResponse: CreatePropietarioResponse = new CreatePropietarioResponse();

  // Flags de estado (mantener compatibilidad)
  esPropietarioNuevo: boolean = false;
  agregaPropietario: boolean = false;
  nuevoPropietario: boolean = false;
  editaPropietario: boolean = false;
  comision: any;

  // Getter para acceso fácil a controles del form
  get f() { return this.propietarioForm.controls; }

  constructor(
    private propietariosService: PropietariosService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private aRoute: ActivatedRoute,
    private personasService: PersonasService,
    private router: Router,
    private dialog: MatDialog
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.personaDetailDto = this.personasService.getPersona();
    this.getAllTipoLiquidacion();
    this.getPropietarioByIdPersona(this.personaDetailDto.id);
  }

  /**
   * Inicializa el formulario con validaciones mejoradas
   */
  private initializeForm(): void {
    this.propietarioForm = this.formBuilder.group({
      Comision: ['', [
        Validators.required,
        Validators.min(0.01),
        Validators.max(100)
      ]],
      CuitCuil: ['', [
        Validators.pattern(/^([0-9]{11}|[0-9]{2}-[0-9]{8}-[0-9]{1})$/)
      ]],
      NroCuenta: [''],
      Cbu: ['', [
        Validators.pattern(/^[0-9]{22}$/)
      ]],
      Alias: [''],
      TipoLiquidacion: ['', [Validators.required]],
    });
  }

  /**
   * Activa el modo de edición
   */
  activarEdicion(): void {
    this.modoEdicion = true;
    // Guardar datos originales para poder cancelar
    this.datosOriginales = { ...this.propietarioForm.value };
  }

  /**
   * Cancela la edición y restaura datos originales
   */
  cancelarEdicion(): void {
    this.modoEdicion = false;
    // Restaurar datos originales
    if (this.datosOriginales) {
      this.propietarioForm.patchValue(this.datosOriginales);
      this.setearValoresAuxiliares();
    }
    this.datosOriginales = null;
  }

  /**
   * Setea valores auxiliares después de cancelar
   */
  private setearValoresAuxiliares(): void {
    if (this.datosOriginales) {
      this.idTipoLiquidacion = this.datosOriginales.TipoLiquidacion || 0;
    }
  }

  /**
   * Obtiene todos los tipos de liquidación
   */
  getAllTipoLiquidacion(): void {
    this.spinner.show('spTipoLiquidacion');

    this.propietariosService.getAllTiposLiquidacion()
      .subscribe({
        next: (data) => {
          this.spinner.hide('spTipoLiquidacion');
          this.lstTipoLiquidacionModel = data;
        },
        error: (error) => {
          this.spinner.hide('spTipoLiquidacion');
          this.snackBar.showError(error, "Error");
        }
      });
  }

  /**
   * Maneja la selección del tipo de liquidación
   */
  selectTipoLiquidacion(event: any): void {
    this.idTipoLiquidacion = event.value;
  }

  /**
   * Maneja la selección del banco
   */
  selectBanco(event: any): void {
    this.banco = event;
  }

  /**
   * Resetea el formulario
   */
  onReset(): void {
    this.propietarioForm.reset();
    this.idTipoLiquidacion = 0;
    this.banco = new BaseDto();
  }

  /**
   * Maneja el envío del formulario
   */
  onSubmit(): void {
    if (this.propietarioForm.invalid) {
      this.marcarCamposComoTocados();
      this.snackBar.showError('Por favor, complete todos los campos requeridos correctamente.', 'Error de validación');
      return;
    }

    this.setUpdatePropietarioRequest();
    this.updatePropietario(this.updatePropietarioRequest);
  }

  /**
   * Marca todos los campos como tocados para mostrar errores
   */
  private marcarCamposComoTocados(): void {
    Object.keys(this.propietarioForm.controls).forEach(key => {
      this.propietarioForm.get(key)?.markAsTouched();
    });
  }

  /**
   * Actualiza el propietario
   */
  updatePropietario(updatePropietarioRequest: UpdatePropietarioRequest): void {
    this.spinner.show('spUpdatePropietario');

    this.propietariosService.update(updatePropietarioRequest)
      .subscribe({
        next: (data) => {
          this.spinner.hide('spUpdatePropietario');
          this.snackBar.showSuccess('Se actualizó el propietario correctamente.', "Éxito");
          this.modoEdicion = false;
          this.datosOriginales = null;
          // Recargar datos actualizados
          this.getPropietarioByIdPersona(this.personaDetailDto.id);
        },
        error: (error) => {
          this.spinner.hide('spUpdatePropietario');
          this.snackBar.showError(error, "Error");
        }
      });
  }

  /**
   * Prepara el request para actualización
   */
  setUpdatePropietarioRequest(): void {
    this.updatePropietarioRequest = new UpdatePropietarioRequest();

    this.updatePropietarioRequest.id = this.idPropietario;
    this.updatePropietarioRequest.id_Persona = this.personaDetailDto.id;

    // Banco
    this.updatePropietarioRequest.id_Banco = this.banco.id > 0 ? this.banco.id : null;

    // Tipo de liquidación
    this.updatePropietarioRequest.id_TipoLiquidacion = this.idTipoLiquidacion > 0
      ? this.idTipoLiquidacion
      : null;

    // Campos del formulario
    this.updatePropietarioRequest.comision = this.f['Comision'].value;
    this.updatePropietarioRequest.alias = this.f['Alias'].value?.trim() || null;
    this.updatePropietarioRequest.cbu = this.f['Cbu'].value?.trim() || null;
    this.updatePropietarioRequest.nroCuenta = this.f['NroCuenta'].value?.trim() || null;
    this.updatePropietarioRequest.cuit = this.f['CuitCuil'].value?.trim() || null;
  }

  /**
   * Configura el formulario con los datos del propietario
   */
  setFormulario(propietario: PropietarioDto): void {
    this.personaDetailDto.id = propietario.persona.id;

    // Configurar banco
    this.banco = isFalsy(propietario.banco) ? new BaseDto() : propietario.banco;

    // Configurar tipo de liquidación
    if (isFalsy(propietario.tipoLiquidacion)) {
      this.idTipoLiquidacion = 0;
      this.propietarioForm.controls['TipoLiquidacion'].setValue('');
    } else {
      this.idTipoLiquidacion = propietario.tipoLiquidacion.id;
      this.propietarioForm.controls['TipoLiquidacion'].setValue(propietario.tipoLiquidacion.id);
    }

    // Configurar campos del formulario
    this.idPropietario = propietario.id;
    this.propietarioForm.patchValue({
      Comision: propietario.comision || '',
      Alias: propietario.alias || '',
      Cbu: propietario.cbu || '',
      NroCuenta: propietario.nroCuenta || '',
      CuitCuil: propietario.cuit || ''
    });
  }

  /**
   * Obtiene el propietario por ID de persona
   */
  getPropietarioByIdPersona(idPersona: number): void {
    this.spinner.show('spUpdatePropietario');

    this.propietariosService.getByIdPersona(idPersona)
      .subscribe({
        next: (data) => {
          this.spinner.hide('spUpdatePropietario');
          this.propietario = data;
          this.setFormulario(this.propietario);
          this.propietariosService.setPropietario(data);
        },
        error: (error) => {
          this.spinner.hide('spUpdatePropietario');
          this.snackBar.showError(error, "Error");
        }
      });
  }

  /**
   * Muestra diálogo de confirmación antes de eliminar
   */
   confirmarEliminacion(): void {

      const dialogRef = this.dialog.open(DeleteConfirmationModalComponent, {
        width: '650px',
        disableClose: true,
        data: {
          title: 'Eliminar propietario',
          warningMessage: '¿Está seguro que desea eliminar este propietario? Esta acción no se puede deshacer y puede afectar contratos asociados.',
          confirmationText: 'ELIMINAR',
          isDestructive: true
        }
      });

      dialogRef.afterClosed().subscribe((result: DeleteConfirmationModalResult) => {
        if (result?.confirmed) {
           this.eliminar();
        }
      });
    }

  /**
   * Elimina el propietario
   */
  eliminar(): void {
    this.spinner.show('spUpdatePropietario');

    this.propietariosService.delete(this.propietario.id)
      .subscribe({
        next: (data) => {
          this.spinner.hide('spUpdatePropietario');
          this.snackBar.showSuccess('Propietario eliminado correctamente.', 'Éxito');

          // Actualizar estado de persona
          this.personaDetailDto = this.personasService.getPersona();
          this.personaDetailDto.esPropietario = false;
          this.personasService.setPersona(this.personaDetailDto);

          // Navegar a lista
          this.router.navigate(["/propietario"]);
        },
        error: (error) => {
          this.spinner.hide('spUpdatePropietario');
          this.snackBar.showError(error, "Error");
        }
      });
  }

  /**
   * Función para trackBy en *ngFor
   */
  trackByItems(index: number, item: any): any {
    return item?.id || item;
  }

  /**
   * Verifica si el formulario tiene cambios
   */
  tienecambios(): boolean {
    if (!this.datosOriginales) return false;

    const valoresActuales = this.propietarioForm.value;
    return JSON.stringify(this.datosOriginales) !== JSON.stringify(valoresActuales);
  }

  /**
   * Valida si el CBU tiene el formato correcto
   */
  validarCBU(cbu: string): boolean {
    return /^[0-9]{22}$/.test(cbu);
  }

  /**
   * Valida si el CUIT/CUIL tiene el formato correcto
   */
  validarCUITCUIL(cuitCuil: string): boolean {
    return /^([0-9]{11}|[0-9]{2}-[0-9]{8}-[0-9]{1})$/.test(cuitCuil);
  }
}
